import { ApiError } from '@/lib/apiClient';

/**
 * Every refusal the registers can produce, mapped to a sentence.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 A CODE THAT IS NOT HANDLED HERE REACHES A STAFF MEMBER AS A RAW STRING.
 * ─────────────────────────────────────────────────────────────────────────────
 * The API answers a conflict with `{ "error": "sim_in_use" }` and nothing else —
 * no message, deliberately, because a unique violation's driver detail contains
 * the conflicting IMEI, ICCID or phone number. So the sentence has to be built
 * on this side, and an unmapped code would either render as `sim_in_use` or as
 * nothing at all.
 *
 * The vocabulary below is COPIED FROM THE SERVER'S CLOSED SET, not invented:
 * `InventoryErrorCode` in `api/src/modules/admin/inventoryStore.ts` (nineteen
 * codes as of 18g), plus `ProvisioningError`'s nine, plus `CommissioningError`'s
 * seven, plus `invalid_body`. `KNOWN_CODES` is asserted against that list by
 * `inventoryErrors.test.ts`, so a code added on the server fails a test here
 * rather than surfacing to an operator.
 *
 * 🔴 THAT ASSERTION WAS SILENTLY BLIND FOR TWO SLICES AND IS NOW FIXED. Its
 * parser sliced the server union from its declaration to the first `;`, and the
 * doc comment on `traccar_device_missing` contains one ("...Retrying cannot fix
 * it; the register is lying."). So it read only the first FOURTEEN members and
 * agreed with a list that was five short — a green test asserting nothing about
 * the codes it was added to guard. The parser now strips comments first, and a
 * case asserts it reaches the LAST member of the union.
 */

/** The store's closed vocabulary, verbatim. */
export const INVENTORY_CODES = [
  'assignment_overlaps_history',
  'vehicle_already_has_device',
  'device_in_use',
  'device_already_has_sim',
  'sim_in_use',
  'imei_taken',
  'iccid_taken',
  'msisdn_taken',
  'invalid_interval',
  'not_found',
  'device_not_in_service',
  'device_has_no_traccar_device',
  'sim_already_activated',
  'nothing_to_remove',
  // 18e/18f. These two were on the server union for two slices and were NEVER
  // in this list — the test that exists to catch exactly that could not see
  // them, because its parser stopped at a semicolon inside a doc comment. See
  // `inventoryErrors.test.ts`.
  'traccar_device_missing',
  'traccar_group_move_failed',
  // 18g — the way back. `device_still_installed` is the retire refusal,
  // `not_commissioned` the decommission refusal, `traccar_delete_failed` the
  // retryable Traccar failure on the DELETE.
  'device_still_installed',
  'not_commissioned',
  'traccar_delete_failed',
  // Slice 30 — the install record's three refusals. These are on the server's
  // `InventoryErrorCode` union (`inventoryStore.ts`), so the parity test above
  // demands they appear here too: `no_open_assignment` (create with no open
  // assignment), `installation_exists` (a second record for one assignment),
  // `installation_closed` (edit or confirm on a closed assignment).
  'no_open_assignment',
  'installation_exists',
  'installation_closed',
  // 🔴 SLICE 52 — the SIM register/activate refusal for "one phone, one identity"
  // (Mahad): a number a tenant user or staff member signs in with cannot also be a
  // tracker SIM. It is a MEMBER of the server's `InventoryErrorCode` union (both
  // SIM create and activate throw `InventoryError('msisdn_is_login')`), so it lives
  // here, not in `WIRE_ONLY_CODES` — the parity test reads that union and demands it.
  'msisdn_is_login',
] as const;

/**
 * `ProvisioningError`'s codes, verbatim — the wizard and the assignment dialog
 * share this mapper.
 *
 * 🔴 SLICE 18f CHANGED THIS UNION ON THE SERVER AND THE TEST WENT RED HERE,
 * WHICH IS EXACTLY WHAT IT IS FOR. `device_already_registered` is GONE — a
 * commissioned tracker is now MOVED into the tenant's group rather than
 * refused — and two codes for the ways that move can fail took its place:
 * `traccar_group_move_failed` (ours, retryable) and `traccar_device_missing`
 * (the register points at a Traccar device that no longer exists).
 */
export const PROVISIONING_CODES = [
  'tenant_unknown',
  'tenant_has_no_group',
  'duplicate_imei',
  'device_unknown',
  'traccar_group_move_failed',
  'traccar_device_missing',
  'traccar_failed',
  'db_failed',
  'checks_failed',
] as const;

/**
 * `CommissioningError`'s codes, verbatim (slice 18f).
 *
 * Five of the seven are already in the unions above; the two that are not
 * (`device_already_commissioned`, `device_has_no_sim`) are the whole reason this
 * list exists. An unmapped one would reach a super-admin as the raw string
 * `device_has_no_sim` at the moment they are trying to put hardware on a bench.
 */
export const COMMISSIONING_CODES = [
  'not_found',
  'device_not_in_service',
  'device_already_commissioned',
  'device_in_use',
  'device_has_no_sim',
  'imei_taken',
  'traccar_failed',
] as const;

/**
 * Codes MINTED BY A ROUTE, not carried by any store's error union — so they
 * cannot live in the lists above (the parity test asserts those match a server
 * union member-for-member). `checks_failed` is the precedent (it is in
 * `PROVISIONING_CODES` for historical reasons); `invalid_phone` is slice 30's:
 * the install-record create/patch normalises the installer phone and answers
 * `400 invalid_phone` on a bad one, and without this it would reach a staff
 * member as `inventory.error.unknown`.
 *
 * 🔴 SLICE 52 — `msisdn_is_login` (the SIM login-clash refusal) is NOT here: it is
 * a real member of the server's `InventoryErrorCode` union, so it belongs in
 * `INVENTORY_CODES` and the parity test enforces that. See the note there.
 */
export const WIRE_ONLY_CODES = ['invalid_phone'] as const;

export const KNOWN_CODES: readonly string[] = [
  ...new Set<string>([
    ...INVENTORY_CODES,
    ...PROVISIONING_CODES,
    ...COMMISSIONING_CODES,
    ...WIRE_ONLY_CODES,
    'invalid_body',
  ]),
];

/**
 * The i18n key for an error thrown by any inventory or provisioning mutation.
 *
 * Returns a key, never a sentence, so the caller renders it through `t()` and
 * Somali is a first-class outcome rather than a retrofit. Anything unrecognised
 * — a 500, a network failure, a body that is not the shape we expect — becomes
 * `inventory.error.unknown`, which says the operation failed and does not
 * pretend to know why.
 */
export function inventoryErrorKey(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body;
    const code =
      body !== null && typeof body === 'object' && typeof (body as { error?: unknown }).error === 'string'
        ? (body as { error: string }).error
        : null;
    if (code !== null && KNOWN_CODES.includes(code)) return `inventory.error.${code}`;
    if (err.status === 403) return 'inventory.error.forbidden';
  }
  return 'inventory.error.unknown';
}
